import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Coins, AlertTriangle, Infinity as InfinityIcon, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

/**
 * Pre-action AI Credit confirmation dialog.
 * Props: open, onOpenChange(bool), actionKey, title, onConfirm()
 */
const CreditConfirmDialog = ({ open, onOpenChange, actionKey, title, onConfirm }) => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && actionKey) {
      setInfo(null);
      api.revCreditCheck({ action_key: actionKey }, getAuthHeaders())
        .then((res) => setInfo(res.data))
        .catch(() => setInfo({ sufficient: true, exempt: true, credit_cost: 0 }));
    }
  }, [open, actionKey]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await api.revCreditConsume({ action_key: actionKey }, getAuthHeaders());
      window.dispatchEvent(new Event('credits:refresh'));
    } catch (e) { /* fail-open */ }
    setLoading(false);
    onOpenChange(false);
    onConfirm?.();
  };

  const blocked = info && info.sufficient === false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="credit-confirm-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-500" />
            {title || 'Confirm AI Action'}
          </DialogTitle>
          <DialogDescription>
            This action consumes AI credits from your wallet.
          </DialogDescription>
        </DialogHeader>

        {!info ? (
          <div className="flex items-center justify-center py-6 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Checking balance…
          </div>
        ) : info.exempt ? (
          <div className="flex items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
            <InfinityIcon className="w-6 h-6 text-emerald-600" />
            <div>
              <p className="font-semibold text-emerald-800">Unlimited Access</p>
              <p className="text-sm text-emerald-700">Master tenant — no credits consumed.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="credit-confirm-details">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Cost" value={info.credit_cost} color="text-slate-900" />
              <Stat label="Available" value={info.available_credits} color="text-blue-600" />
              <Stat label="After" value={info.after} color="text-slate-900" />
            </div>
            {blocked && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3" data-testid="credit-insufficient-warning">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                <p className="text-sm text-red-700">
                  Insufficient credits. Please top up to continue.
                </p>
              </div>
            )}
            <p className="text-xs text-slate-400 capitalize">Enforcement mode: {info.mode}</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="credit-cancel-btn">
            Cancel
          </Button>
          {blocked ? (
            <Button onClick={() => { onOpenChange(false); navigate('/billing'); }} data-testid="credit-topup-btn"
              className="bg-amber-500 hover:bg-amber-600">
              Buy Credits
            </Button>
          ) : (
            <Button onClick={handleConfirm} disabled={loading} data-testid="credit-confirm-btn"
              style={{ backgroundColor: '#1e3a5f' }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm & Proceed'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="rounded-lg bg-slate-50 border border-slate-200 py-2">
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
  </div>
);

export default CreditConfirmDialog;
